import { InitData } from '../../core'
import { t, dayNames } from '../../core/i18n'

interface Props {
  initData: InitData
  title?: string | null
  showOpeningHours?: boolean
}

const SAFE_MAP_RE = /^https:\/\/(www\.google\.com\/maps\/embed|maps\.google\.com|www\.openstreetmap\.org)/

const DAY_ORDER = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag',
]


// Index 0 = Sunday, matching dayNames(). Tenants store opening hours with
// either English or Danish keys, so both spellings map to the same weekday.
const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday']
const DAY_KEYS_DA = ['søndag','mandag','tirsdag','onsdag','torsdag','fredag','lørdag']

/** Day name for an opening-hours key, via Intl so every locale is covered. */
function localizeDay(key: string, locale: string): string {
  const k = key.toLowerCase()
  const idx = DAY_KEYS.indexOf(k) >= 0 ? DAY_KEYS.indexOf(k) : DAY_KEYS_DA.indexOf(k)
  if (idx >= 0) return dayNames(locale)[idx]
  return key
}

/**
 * Coerce the unknown openingHours field into [label, value] pairs.
 * Accepts:
 *   - Record<string, string>  → entries in DAY_ORDER first, then alpha
 *   - Array<[string, string]> → as-is
 *   - Array<{day, hours}>     → mapped
 *   - anything else           → []
 */
function normalizeOpeningHours(raw: unknown): Array<[string, string]> {
  if (!raw) return []

  if (Array.isArray(raw)) {
    return raw
      .map((item): [string, string] | null => {
        if (Array.isArray(item) && item.length >= 2) {
          return [String(item[0]), String(item[1])]
        }
        if (item && typeof item === 'object') {
          const obj = item as Record<string, unknown>
          const day = obj.day ?? obj.label ?? obj.name
          const hours = obj.hours ?? obj.value ?? obj.time
          if (typeof day === 'string' && typeof hours === 'string') {
            return [day, hours]
          }
        }
        return null
      })
      .filter((x): x is [string, string] => x !== null)
  }

  if (typeof raw === 'object') {
    const entries = Object.entries(raw as Record<string, unknown>)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')

    entries.sort((a, b) => {
      const ai = DAY_ORDER.indexOf(a[0].toLowerCase())
      const bi = DAY_ORDER.indexOf(b[0].toLowerCase())
      if (ai === -1 && bi === -1) return a[0].localeCompare(b[0])
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
    return entries
  }

  return []
}

/**
 * ContactInfoWidget — mirrors HS-native ContactInfoWidget styling.
 *
 * Two-column layout:
 *   - Left: address + opening hours
 *   - Right: email + phone
 *   - Map iframe (sandboxed) below if mapEmbedUrl is allowlisted
 *
 * No async fetch — all data comes from initData.contactInfo.
 */
export function ContactInfoWidget({ initData, title = null, showOpeningHours = true }: Props) {
  const info = initData.contactInfo
  const locale = initData.locale || 'da'
  const str = t(locale)

  const hours = showOpeningHours ? normalizeOpeningHours(info?.openingHours) : []
  const safeMapUrl = info?.mapEmbedUrl && SAFE_MAP_RE.test(info.mapEmbedUrl) ? info.mapEmbedUrl : null
  const hasAnyInfo = info && (
    info.email || info.phone || info.address || info.city || info.postalCode ||
    hours.length > 0 || safeMapUrl
  )
  if (!hasAnyInfo) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📍</div>
        <p>{t(locale).noContactInfo}</p>
      </div>
    )
  }

  const hasAddress = Boolean(info.address || info.city)
  const hasContact = Boolean(info.email || info.phone)
  const hasHours = hours.length > 0

  return (
    <div>
      <style>{contactStyles}</style>
      {title && <h2 class="hsw-contact-info-title">{title}</h2>}
      <div class="hsw-contact-card">
        <div class="hsw-contact-grid">
          {(hasAddress || hasHours) && (
            <div class="hsw-contact-col">
              {hasAddress && (
                <div class="hsw-contact-row">
                  <span class="hsw-contact-icon" aria-hidden="true">📍</span>
                  <div class="hsw-contact-body">
                    <div class="hsw-contact-label">{str.address}</div>
                    {info.address && <div class="hsw-contact-value">{info.address}</div>}
                    {(info.postalCode || info.city) && (
                      <div class="hsw-contact-value">
                        {info.postalCode}{info.postalCode && info.city ? ' ' : ''}{info.city}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {hasHours && (
                <div class="hsw-contact-row">
                  <span class="hsw-contact-icon" aria-hidden="true">🕐</span>
                  <div class="hsw-contact-body">
                    <div id="hsw-hours-label" class="hsw-contact-label">{t(locale).openingHours}</div>
                    <table class="hsw-hours-table" aria-labelledby="hsw-hours-label">
                      <tbody>
                        {hours.map(([day, value]) => (
                          <tr key={day}>
                            <td class="hsw-hours-day">{localizeDay(day, locale)}</td>
                            <td class="hsw-hours-value">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {hasContact && (
            <div class="hsw-contact-col">
              {info.email && (
                <div class="hsw-contact-row">
                  <span class="hsw-contact-icon" aria-hidden="true">✉️</span>
                  <div class="hsw-contact-body">
                    <div class="hsw-contact-label">{str.fieldEmail}</div>
                    <a class="hsw-contact-link" href={`mailto:${info.email}`}>
                      {info.email}
                    </a>
                  </div>
                </div>
              )}
              {info.phone && (
                <div class="hsw-contact-row">
                  <span class="hsw-contact-icon" aria-hidden="true">📞</span>
                  <div class="hsw-contact-body">
                    <div class="hsw-contact-label">{str.fieldPhone}</div>
                    <a class="hsw-contact-link" href={`tel:${info.phone}`}>
                      {info.phone}
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {safeMapUrl && (
          <div class="hsw-contact-map">
            <iframe
              src={safeMapUrl}
              title={str.mapOf.replace('{tenant}', initData.tenantName)}
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups"
              referrerpolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
      </div>
    </div>
  )
}

const contactStyles = `
  .hsw-contact-info-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 980px;
    letter-spacing: -0.01em;
  }
  .hsw-contact-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2rem;
    max-width: 1100px;
    margin: 0 auto;
  }
  .hsw-contact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 2rem;
  }
  .hsw-contact-col {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  .hsw-contact-row {
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
  }
  .hsw-contact-icon {
    font-size: 1.25rem;
    line-height: 1.4;
    width: 28px;
    flex-shrink: 0;
    text-align: center;
  }
  .hsw-contact-body {
    flex: 1;
    min-width: 0;
  }
  .hsw-contact-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--yoga-text-muted, #737373);
    margin-bottom: 0.35rem;
  }
  .hsw-contact-value {
    font-size: 0.95rem;
    color: var(--yoga-text, #0a0a0a);
    line-height: 1.5;
  }
  .hsw-contact-link {
    font-size: 0.95rem;
    color: var(--yoga-primary, #FF6B35);
    text-decoration: none;
    word-break: break-word;
    transition: opacity .2s;
  }
  .hsw-contact-link:hover {
    opacity: 0.75;
    text-decoration: underline;
  }
  .hsw-hours-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  .hsw-hours-table tr + tr td {
    border-top: 1px solid var(--yoga-card-border, #f0f0f0);
  }
  .hsw-hours-day {
    padding: 0.4rem 0.75rem 0.4rem 0;
    color: var(--yoga-text, #404040);
    text-transform: capitalize;
    white-space: nowrap;
  }
  .hsw-hours-value {
    padding: 0.4rem 0;
    color: var(--yoga-text-muted, #525252);
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .hsw-contact-map {
    margin-top: 2rem;
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    aspect-ratio: 16 / 7;
    min-height: 220px;
  }
  .hsw-contact-map iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
  @media (max-width: 640px) {
    .hsw-contact-card { padding: 1.5rem; }
    .hsw-contact-grid { gap: 1.5rem; }
    .hsw-contact-map { aspect-ratio: 4 / 3; }
  }
`
